<template>
  <section class="h-full mx-4">
    <BaseTable
      :headers="alterCodeTypesHeaders"
      :list="alterCodeTypes"
      @deleteMultiselect="onDeleteMultiselect"
      @add-new="onAddNewAlterCodeType"
      @refresh-data="refreshData"
      table-id="alter-code-types"
      clickable
      @table-item-clicked="onItemClick"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #actions="{ item }: any">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="item.isFromManager"
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteRow(item as AlterCodeType)" />
        </div>
      </template>

      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('baseObject.isActive')
            }}</label>
            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>

    <AlterCodeTypeModalCreateEdit
      v-if="createEditModalActive"
      :alter-code-type="alterCodeType"
      :title="modalEditCreateTitle()"
      :mode="createEditModalMode"
      @close-modal="createEditModalActive = false"
      @save="onSave" />

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { getAlterCodeTypes, removeAlterCodeTypesByIds } from '@/services/api/materials';
import { useToastStore } from '@/stores/toast';
import { filterActive, filterData, sortData } from '@/utils/table';
import AlterCodeTypeModalCreateEdit from '@/components/materials/AlterCodeTypeModalCreateEdit.vue';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import ActionButton from '@/components/common/ActionButton.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';

import { eAlterCodeType, type AlterCodeType } from '@/types/Materials';
import type { TableFilterData } from '@/types/Base';
import { useI18n } from 'vue-i18n';
import { useLookupsStore } from '@/stores/lookups';

const { t: $t } = useI18n({ useScope: 'global' });
const alterCodeTypes = ref<AlterCodeType[]>([]);
const { reloadLookups } = useLookupsStore();

const alterCodeTypesHeaders = [
  { value: 'name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'code', text: $t('baseObject.code'), type: 'text', active: true },
  { value: 'alterCodeType', text: $t('material.alterCodeType'), type: 'text', active: true },
  { value: 'hasMixInformation', text: $t('material.hasMixInfo'), type: 'check', active: true },
  { value: 'prefixIncluded', text: $t('series.prefix'), type: 'check', active: true },
  { value: 'isFromManager', text: $t('baseObject.isFromManager'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

function initializeAlterCodeType(): AlterCodeType {
  return {
    id: uuidv4(),
    eType: 0,
    hasMixInformation: false,
    prefixIncluded: false,
    name: '',
    code: '',
    isFromManager: true,
    isActive: true,
    alterCodeType: '',
  };
}

onMounted(async () => {
  const res = await getAlterCodeTypes();
  alterCodeTypes.value = populateAlterCodeTypeField(res);
});

function getAlterCodeTypeString(eType: number): string {
  switch (eType) {
    case eAlterCodeType.CompanyType:
      return eAlterCodeType.CompanyType.toString();
    case eAlterCodeType.Competitor:
      return eAlterCodeType.Competitor.toString();
    case eAlterCodeType.Other:
      return eAlterCodeType.Other.toString();
    default:
      return 'Unknown';
  }
}

function populateAlterCodeTypeField(alterCodeTypes: AlterCodeType[]): AlterCodeType[] {
  return alterCodeTypes.map((type) => ({
    ...type,
    alterCodeType: type.eType != null ? getAlterCodeTypeString(type.eType) : '',
  }));
}

const toastStore = useToastStore();

async function onDeleteMultiselect(items: AlterCodeType[]) {
  const idsToDelete = items.filter((item) => item.isFromManager).map((item) => item.id);
  const res = await removeAlterCodeTypesByIds(idsToDelete);
  if (!res && alterCodeTypes.value)
    alterCodeTypes.value = alterCodeTypes.value.filter((type) => !idsToDelete.includes(type.id));
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });

  await reloadLookups(['alterCodeTypes']);
}

async function onDeleteSingle() {
  modalDeleteRow.value = false;
  if (!deleteRowItem.value) return;
  const idToDelete = deleteRowItem.value.id;
  const res = await removeAlterCodeTypesByIds([idToDelete]);
  if (!res && alterCodeTypes.value)
    alterCodeTypes.value = alterCodeTypes.value.filter((type) => type.id !== idToDelete);
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });

  await reloadLookups(['alterCodeTypes']);
  await reloadLookups(['alterCodeTypes']);
}

const alterCodeType = ref<AlterCodeType>(initializeAlterCodeType());

const createEditModalActive = ref(false);
const createEditModalMode = ref<'add' | 'edit'>('add');

function onAddNewAlterCodeType() {
  alterCodeType.value = initializeAlterCodeType();
  createEditModalActive.value = true;
  createEditModalMode.value = 'add';
}

function onItemClick(item: AlterCodeType) {
  alterCodeType.value = item;
  createEditModalMode.value = 'edit';
  createEditModalActive.value = true;
}

async function onSave(modifiedData: AlterCodeType) {
  if (!alterCodeTypes.value) return;

  const index = alterCodeTypes.value.findIndex((item) => item.id === modifiedData.id);
  if (index !== -1) alterCodeTypes.value[index] = modifiedData;
  else alterCodeTypes.value.push(modifiedData);
  alterCodeTypes.value = populateAlterCodeTypeField(alterCodeTypes.value);
  createEditModalActive.value = false;

  await reloadLookups(['alterCodeTypes']);
}

const filterIsActive = ref<boolean | undefined>();

async function refreshData(data: TableFilterData) {
  const response = await getAlterCodeTypes();
  let processedData = populateAlterCodeTypeField(response);

  if (data.query) {
    processedData = filterData(processedData, data.query, ['name', 'code', 'alterCodeType']);
  }

  if (data.column !== undefined && data.order) {
    processedData = sortData(processedData, data.column, data.order, alterCodeTypesHeaders);
  }

  if (data.isActive !== undefined) processedData = filterActive(processedData, data.isActive);

  alterCodeTypes.value = processedData;
}

const modalDeleteRow = ref(false);
const deleteRowItem = ref<AlterCodeType>();
function onDeleteRow(item: AlterCodeType) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

function modalEditCreateTitle() {
  if (alterCodeType.value && !alterCodeType.value.isFromManager) return $t('common.actions.view');
  return createEditModalMode.value === 'edit'
    ? $t('common.actions.edit')
    : $t('common.actions.create');
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value ?? undefined;
  refreshData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  refreshData({});
}
</script>
